import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Wallet, Users, LineChart, ShieldCheck, FileText, Mail, X, CheckCircle2 } from "lucide-react";
import api, { fmtUSD, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FEATURES = [
  { icon: Wallet, h: "Deal Accounts", p: "Spin up a dedicated account for every deal you run — goal, term, target return, and status tracked in one place." },
  { icon: Users, h: "Investor Ledger", p: "Keep a living record of every investor: contact details, banking for payouts, and their full investment history." },
  { icon: LineChart, h: "Live Raise Tracking", p: "Watch commitments roll in against your goal in real time, and auto-close the round the moment you hit target." },
  { icon: FileText, h: "Polished Packets", p: "Hand invited investors a clean, confidential deal room and a downloadable PDF packet — no clutter, no noise." },
  { icon: Mail, h: "Investor Comms", p: "Send templated updates, agreements, and reminders with attachments — straight to the right contributors." },
  { icon: ShieldCheck, h: "Private by Default", p: "Every deal room sits behind investor login. Only the people you invite ever see the numbers." },
];

const STEPS = [
  { n: "01", h: "Open a deal account", p: "Define the raise — target amount, fixed return, and term. It becomes your live deal room." },
  { n: "02", h: "Invite your investors", p: "Share the passcode. Invited investors review the packet and commit an allocation in seconds." },
  { n: "03", h: "Track & close", p: "Follow commitments and wires from the dashboard. The round closes itself when the goal is met." },
];

export default function Landing({ onEnterDeck, onAdmin }) {
  const [progress, setProgress] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    api.get("/public/progress").then(({ data }) => setProgress(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-background border-b border-black">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="mono text-sm font-semibold tracking-[0.18em]">
            CANIHAVEMONEY&nbsp;LLC
          </button>
          <nav className="flex items-center gap-6 sm:gap-9">
            <button data-testid="nav-platform" onClick={() => document.getElementById("platform")?.scrollIntoView({ behavior: "smooth" })} className="hidden sm:inline mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black tick-underline">
              Platform
            </button>
            <button data-testid="nav-enter-deck" onClick={onEnterDeck} className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <Lock size={13} /> Investor Login
            </button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative grain overflow-hidden border-b border-black">
        <div className="max-w-6xl mx-auto px-6 py-24 sm:py-32 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 border border-black px-3 py-1 mb-8">
              <span className="mono text-[10px] uppercase tracking-[0.3em]">Private Capital · Run Your Own Raise</span>
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
              An investment<br />account for your deals.<br />
              <span className="text-neutral-400">Track your balances and payments.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg sm:text-xl font-light leading-relaxed text-neutral-700">
              Canihavemoney LLC runs confidential, receivables-backed financings for a small circle of invited investors — and gives each of them a clean deal room, a live raise tracker, and a full investor ledger behind the scenes.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="hero-enter-deck" onClick={onEnterDeck} className="inline-flex items-center rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 px-8 mono text-xs uppercase tracking-[0.25em]">
                Investor Login <ArrowRight className="ml-2" size={16} />
              </button>
              <button data-testid="hero-join-waitlist" onClick={() => setShowWaitlist(true)} className="inline-flex items-center rounded-none bg-transparent text-black hover:bg-black hover:text-white border border-black transition-colors h-14 px-8 mono text-xs uppercase tracking-[0.25em]">
                Join to See Available Investments
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LIVE STAT STRIP */}
      {progress && (
        <section className="border-b border-black bg-black text-white">
          <div className="max-w-6xl mx-auto px-6 py-10 grid sm:grid-cols-3 gap-8">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Current Raise</div>
              <div className="text-2xl font-black tracking-tighter mt-2">{progress.deal_name || "Live Deal"}</div>
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Committed to Date</div>
              <div className="text-3xl font-black tracking-tighter mt-2">{fmtUSD(progress.committed)}</div>
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Of Goal</div>
              <div className="text-3xl font-black tracking-tighter mt-2">{progress.percent}%{progress.closed ? " · Closed" : ""}</div>
            </div>
          </div>
        </section>
      )}

      {/* PLATFORM / FEATURES */}
      <section id="platform" className="border-b border-black scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
          <div className="flex items-center gap-4 mb-14">
            <span className="mono text-xs text-muted-foreground">[01]</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter">One place to run the whole raise</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 border-l border-t border-black">
            {FEATURES.map((f) => (
              <div key={f.h} className="border-r border-b border-black p-8">
                <f.icon size={24} strokeWidth={1.5} />
                <h3 className="text-xl font-semibold tracking-tight mt-5">{f.h}</h3>
                <p className="mt-3 font-light leading-relaxed text-neutral-700">{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-black bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
          <div className="flex items-center gap-4 mb-14">
            <span className="mono text-xs text-neutral-500">[02]</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter">How it works</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/20 border border-white/20">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-black p-8">
                <div className="mono text-xs text-neutral-500">{s.n}</div>
                <h3 className="text-2xl font-semibold tracking-tight mt-3">{s.h}</h3>
                <p className="mt-3 font-light leading-relaxed text-neutral-400">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-black">
        <div className="max-w-6xl mx-auto px-6 py-24 text-center">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter max-w-3xl mx-auto">Ready to see the current opportunity?</h2>
          <p className="mt-6 max-w-xl mx-auto text-lg font-light text-neutral-700">The live deal room is invitation-only. Sign in to your investor portal to review the full packet, your positions, and available investments.</p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <button data-testid="cta-enter-deck" onClick={onEnterDeck} className="inline-flex items-center rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 px-10 mono text-xs uppercase tracking-[0.25em]">
              <Lock size={14} className="mr-2" /> Investor Login
            </button>
            <button data-testid="cta-join-waitlist" onClick={() => setShowWaitlist(true)} className="inline-flex items-center rounded-none bg-transparent text-black hover:bg-black hover:text-white border border-black transition-colors h-14 px-10 mono text-xs uppercase tracking-[0.25em]">
              Join to See Available Investments
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-black">
        <div className="max-w-6xl mx-auto px-6 py-14 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            © {new Date().getFullYear()} Canihavemoney LLC · 2067 McDonald Ave, Brooklyn, NY 11223
          </span>
          <button data-testid="footer-admin-btn" onClick={onAdmin} className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-black underline underline-offset-4 text-left">
            Admin Dashboard
          </button>
        </div>
      </footer>
    </div>
  );
}


function WaitlistModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your name");
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/public/waitlist", { name: name.trim(), email: email.trim() });
      setDone(true);
      toast.success("You're on the list");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="waitlist-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-background border border-black">
        <div className="flex items-center justify-between border-b border-black px-6 py-4">
          <span className="mono text-xs uppercase tracking-[0.25em]">Available Investments</span>
          <button data-testid="waitlist-modal-close" onClick={onClose} className="text-muted-foreground hover:text-black"><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-8" data-testid="waitlist-modal-done">
            <CheckCircle2 size={30} className="mb-4" />
            <h3 className="text-2xl font-black tracking-tighter">You're on the list</h3>
            <p className="mt-3 font-light text-neutral-700">We'll reach out first when a new allocation or deal opens up.</p>
            <button onClick={onClose} className="mt-6 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-12 px-8 mono text-xs uppercase tracking-[0.25em]">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8" data-testid="waitlist-modal-form">
            <p className="text-base font-light leading-relaxed text-neutral-700 mb-6">Join the list to be notified about available investments and new deals.</p>
            <label className="mono text-xs uppercase tracking-[0.2em]">Full name</label>
            <input data-testid="waitlist-modal-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-none border border-black h-12 mt-2 mb-5 px-3 focus:outline-none focus:border-black" placeholder="Jane Q. Investor" />
            <label className="mono text-xs uppercase tracking-[0.2em]">Email</label>
            <input data-testid="waitlist-modal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-none border border-black h-12 mt-2 px-3 focus:outline-none focus:border-black" placeholder="jane@email.com" />
            <button type="submit" data-testid="waitlist-modal-submit" disabled={busy} className="mt-6 w-full rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-14 mono text-xs uppercase tracking-[0.25em] disabled:opacity-50">
              {busy ? "Joining…" : "Join the List"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
